import React, { Suspense, lazy, useMemo } from 'react';
import { FeatureNavContext } from '../../hooks/useFeatureNav';
import { Loading } from '../../components/aa';

/*
 * The platform surfaces, mounted in the panel that opened them.
 *
 * A step anchored to a real feature used to navigate: Continue closed the
 * panel and dropped the person on a full page, which is the one thing this
 * flow promises not to do. These are the same pages against the same
 * endpoints, reused rather than reimplemented, so there is no second version of
 * a dive, a builder or an org chart to keep in step with the first.
 *
 * Every page below reads its own data and holds its own state, so it is
 * mounted whole. Two of them read the URL for which variant to show; they take
 * `panelRoute` instead when there is no URL to read, and are unchanged
 * otherwise.
 *
 * Loaded through `lazy` so a step's anchor pulls its surface in on demand.
 * That splits nothing today: the hubs in pages/aai/hubs.tsx import the same
 * pages statically, so they are already in the main bundle and each import()
 * resolves on the next tick. It costs nothing and it is the right shape for
 * when those static imports go.
 */

export interface FeaturePanelProps {
  /** The anchor's route, path and query, for a page that reads the URL. */
  panelRoute?: string;
}

type Panel = React.ComponentType<FeaturePanelProps>;

const PANELS: Record<string, Panel> = {
  '/my-tools': lazy(() => import('../aai/insights/MyToolsPage')),
  '/aai/my-tools': lazy(() => import('../aai/insights/MyToolsPage')),
  '/tools': lazy(() => import('../aai/ToolLibraryPage')),
  // The builder on this portal's kit. The older page is still what other roles
  // open at /skill-builder; inside this portal's panels it looked like a
  // different product mid-task.
  '/skill-builder': lazy(() => import('./SkillBuilder')),
  '/skill-builder/workflow': lazy(() => import('./SkillBuilder')),
  '/org-chart': lazy(() => import('../aai/OrgChartPage')),
  '/my-productivity': lazy(() => import('../aai/insights/MyProductivityPage')),
  '/whats-allowed': lazy(() => import('../aai/insights/WhatsAllowedPage')),
  '/aai/vendors': lazy(() => import('../aai/VendorEvaluatorPage')),
  '/aai/impact': lazy(() => import('../aai/insights/ImpactDashboardPage')),
  '/aai/skills': lazy(() => import('../aai/SkillsMarketplacePage')),
  '/requests': lazy(() => import('../aai/RequestsPage')),
  '/my-readiness': lazy(() => import('../aai/MyReadinessPage')),
  '/onboarding': lazy(() => import('../aai/OnboardingPage')),
};

const pathOf = (route: string): string => route.split('?')[0];

/** True when this route can be completed inside a panel. */
export const hasFeaturePanel = (route?: string | null): route is string => {
  if (!route) return false;
  return pathOf(route) in PANELS;
};

export const FeaturePanelBody: React.FC<{
  route: string;
  /** The page asked for another surface; show that one in this panel. */
  onOpen: (route: string) => void;
  /** The page finished with itself. */
  onClose: () => void;
}> = ({ route, onOpen, onClose }) => {
  const nav = useMemo(() => ({ open: onOpen, close: onClose }), [onOpen, onClose]);
  const Panel = PANELS[pathOf(route)];

  if (!Panel) return null;
  return (
    <FeatureNavContext.Provider value={nav}>
      <Suspense fallback={<Loading label="Opening" />}>
        <Panel panelRoute={route} />
      </Suspense>
    </FeatureNavContext.Provider>
  );
};
